/**
 * Web LLM service -- Chrome built-in Prompt API (LanguageModel global).
 * Requires Chrome 138+ with Gemini Nano enabled.
 */
import type { ValidateResult, SuggestResult, SplitResult, ReflectResult, GoalCoverageResult, GeneratedGoal, ActivityCoverageResult, GeneratedActivity } from './llm';
import { validatePrompt, suggestPrompt, splitActivityPrompt, reflectPrompt, explainScorePrompt, goalCoveragePrompt, generateGapGoalsPrompt, activityCoveragePrompt, generateGapActivitiesPrompt, FORMAT_INSTRUCTIONS } from './llm.prompts';
import { resolvePrompt } from './llm.promptRegistry';
import type { PromptKey } from './llm.promptRegistry';

// Chrome Prompt API type augmentation (Chrome 138+)
type LMSession = {
  prompt(input: string): Promise<string>;
  destroy(): void;
};

type LMAvailability = 'available' | 'downloadable' | 'downloading' | 'unavailable';

declare global {
  const LanguageModel: {
    availability(options?: {
      expectedInputs?: Array<{ type: string; languages?: string[] }>;
      expectedOutputs?: Array<{ type: string; languages?: string[] }>;
    }): Promise<LMAvailability>;
    create(options?: {
      systemPrompt?: string;
      temperature?: number;
      topK?: number;
    }): Promise<LMSession>;
  } | undefined;
}

export async function isAvailable(): Promise<boolean> {
  try {
    if (typeof window === 'undefined') return false;
    if (typeof LanguageModel === 'undefined') return false;
    const avail = await LanguageModel.availability();
    // "downloadable"/"downloading" are fine — create() triggers/waits for the download
    return avail !== 'unavailable';
  } catch {
    return false;
  }
}

export async function getModelStatus(): Promise<'ready' | 'downloading' | 'unavailable'> {
  try {
    if (typeof LanguageModel === 'undefined') return 'unavailable';
    const avail = await LanguageModel.availability();
    if (avail === 'available') return 'ready';
    if (avail === 'downloadable' || avail === 'downloading') return 'downloading';
    return 'unavailable';
  } catch {
    return 'unavailable';
  }
}

export async function getUnavailableReason(): Promise<string> {
  if (typeof window === 'undefined') {
    return 'AI features require a browser environment.';
  }
  if (typeof LanguageModel === 'undefined') {
    const ua = navigator.userAgent ?? '';
    // Every browser on iOS is WebKit underneath — Chrome for iOS reports
    // `CriOS/` and can never expose the Prompt API — and on Android the API is
    // desktop-only. Telling a phone user to install Chrome 138 sends them to
    // install something that will not work on the device they are holding.
    // iPadOS Safari presents as a Mac, hence the touch-points check.
    const isIOS =
      /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/.test(ua);
    if (isIOS || isAndroid) {
      return 'On-device AI is not available on phones or tablets. It needs Chrome 138 or later on a desktop computer.';
    }
    const chromeMatch = ua.match(/Chrome\/(\d+)/);
    const isChrome = chromeMatch && !/Edg\/|OPR\//.test(ua);
    if (!isChrome) {
      return 'On-device AI features require Google Chrome (version 138 or later) with Gemini Nano.';
    }
    const version = parseInt(chromeMatch[1], 10);
    if (version < 138) {
      return `On-device AI features require Chrome 138 or later. You are on Chrome ${version} — please update Chrome.`;
    }
    return 'Gemini Nano is not enabled. Visit chrome://flags and enable "Prompt API for Gemini Nano", then restart Chrome.';
  }
  try {
    const avail = await LanguageModel.availability();
    if (avail === 'unavailable') {
      return 'Gemini Nano is not available on this device. Requires Chrome 138+ with sufficient hardware.';
    }
  } catch {
    return 'Unable to check AI availability.';
  }
  return '';
}

async function createSession(
  systemPrompt: string,
  temperature = 0.3
): Promise<LMSession> {
  if (typeof LanguageModel === 'undefined') {
    throw new Error('Chrome Prompt API not available');
  }
  return LanguageModel.create({ systemPrompt, temperature, topK: 10 });
}

export async function validate(
  entityType: string,
  text: string,
  customPrompts?: Record<string, string> | null
): Promise<ValidateResult> {
  const key = `validate_${entityType}` as PromptKey;
  const prompt = resolvePrompt(key, customPrompts, { text }) ?? validatePrompt(entityType, text);
  const systemPrompt = `You are a planning coach. ${FORMAT_INSTRUCTIONS.validate}`;

  const session = await createSession(systemPrompt, 0.2);
  try {
    const response = await session.prompt(prompt);
    return parseValidateResponse(entityType, response);
  } finally {
    session.destroy();
  }
}

export async function suggest(
  entityType: string,
  text: string,
  customPrompts?: Record<string, string> | null
): Promise<SuggestResult> {
  const key = `suggest_${entityType}` as PromptKey;
  const prompt = resolvePrompt(key, customPrompts, { text }) ?? suggestPrompt(entityType, text);
  const systemPrompt = `You are a planning coach. ${FORMAT_INSTRUCTIONS.suggest}`;

  const session = await createSession(systemPrompt, 0.7);
  try {
    const response = await session.prompt(prompt);
    return parseSuggestJson(response);
  } finally {
    session.destroy();
  }
}

export async function splitActivity(
  activityText: string,
  estimatedMinutes: number,
  customPrompts?: Record<string, string> | null
): Promise<SplitResult> {
  const prompt = resolvePrompt('split_activity', customPrompts, { text: activityText, estimatedMinutes: String(estimatedMinutes) }) ?? splitActivityPrompt(activityText, estimatedMinutes);
  const systemPrompt = `You are a planning coach. ${FORMAT_INSTRUCTIONS.splitActivity}`;

  const session = await createSession(systemPrompt, 0.3);
  try {
    const response = await session.prompt(prompt);
    return parseSplitResponse(activityText, response);
  } finally {
    session.destroy();
  }
}

// --- Response Parsers ---

function parseValidateResponse(
  entityType: string,
  response: string
): ValidateResult {
  const upper = response.toUpperCase();
  const isValid = upper.includes('VALID') && !upper.startsWith('INVALID');
  const lines = response.split('\n').filter((l) => l.trim());
  const issues = lines
    .filter((l) => l.trim().startsWith('-'))
    .map((l) => l.trim().replace(/^-\s*/, ''));
  const recLine = lines.find((l) =>
    l.toLowerCase().startsWith('recommendation:')
  );
  const analysisLine = lines.find((l) =>
    l.toLowerCase().startsWith('analysis:')
  );

  return {
    entity_type: entityType,
    is_valid: isValid,
    issues,
    recommendation:
      recLine?.replace(/^recommendation:\s*/i, '').trim() ?? null,
    analysis:
      analysisLine?.replace(/^analysis:\s*/i, '').trim() ??
      response.slice(0, 200),
  };
}

function parseSuggestJson(response: string): SuggestResult {
  try {
    const jsonStr = response.replace(/```json?\s*/g, '').replace(/```/g, '').trim();
    const parsed = JSON.parse(jsonStr);
    const items = Array.isArray(parsed) ? parsed : [parsed];
    const suggestions = items.map((item: any) => ({
      text: String(item.text || ''),
      reasoning: String(item.reasoning || ''),
    }));
    if (suggestions.length > 0 && suggestions[0].text) return { suggestions };
  } catch { /* fall through to fallback */ }
  return { suggestions: [{ text: response.trim(), reasoning: 'AI suggestion' }] };
}

function parseSplitResponse(taskText: string, response: string): SplitResult {
  const tasks: Array<{ text: string; estimated_minutes: number }> = [];
  const lines = response.split('\n').filter((l) => l.trim());
  for (const line of lines) {
    const match = line.trim().match(/^\d+\.\s*(.+?)\s*\((\d+)m\)/);
    if (match) {
      tasks.push({
        text: match[1].trim(),
        estimated_minutes: parseInt(match[2], 10),
      });
    }
  }
  return { original: taskText, suggested_activities: tasks };
}

export async function reflect(scoreSummary: string, customPrompts?: Record<string, string> | null): Promise<ReflectResult> {
  try {
    const prompt = resolvePrompt('reflect', customPrompts, { scoreSummary }) ?? reflectPrompt(scoreSummary);
    const systemPrompt = `You are a supportive productivity coach. ${FORMAT_INSTRUCTIONS.reflect}`;
    const session = await createSession(systemPrompt, 0.5);
    try {
      const response = await session.prompt(prompt);
      return parseReflectResponse(response);
    } finally {
      session.destroy();
    }
  } catch {
    return { reflection: '', highlights: [] };
  }
}

export async function explainScore(scoreSummary: string, customPrompts?: Record<string, string> | null): Promise<string> {
  try {
    const prompt = resolvePrompt('explain_score', customPrompts, { scoreSummary }) ?? explainScorePrompt(scoreSummary);
    const systemPrompt = `You are a productivity coach. ${FORMAT_INSTRUCTIONS.explainScore}`;
    const session = await createSession(systemPrompt, 0.3);
    try {
      const response = await session.prompt(prompt);
      return response.trim();
    } finally {
      session.destroy();
    }
  } catch {
    return '';
  }
}

export async function analyzeGoalCoverage(visionText: string, goalTitles: string[], customPrompts?: Record<string, string> | null): Promise<GoalCoverageResult> {
  try {
    const goalList = goalTitles.map((g, i) => `${i + 1}. ${g}`).join('\n');
    const prompt = resolvePrompt('goal_coverage', customPrompts, { visionText, goalList }) ?? goalCoveragePrompt(visionText, goalTitles);
    const systemPrompt = `You are a planning coach. ${FORMAT_INSTRUCTIONS.goalCoverage}`;
    const session = await createSession(systemPrompt, 0.3);
    try {
      const response = await session.prompt(prompt);
      return parseCoverageJson(response, goalTitles);
    } finally {
      session.destroy();
    }
  } catch {
    return { dimensions: [], summary: '' };
  }
}

export async function generateGapGoals(visionTitle: string, gapDimensions: string[], customPrompts?: Record<string, string> | null): Promise<GeneratedGoal[]> {
  try {
    const gapList = gapDimensions.map((d, i) => `${i + 1}. ${d}`).join('\n');
    const prompt = resolvePrompt('generate_gap_goals', customPrompts, { visionTitle, gapList }) ?? generateGapGoalsPrompt(visionTitle, gapDimensions);
    const systemPrompt = `You are a planning coach. ${FORMAT_INSTRUCTIONS.generateGapGoals}`;
    const session = await createSession(systemPrompt, 0.4);
    try {
      const response = await session.prompt(prompt);
      return parseGapGoalsJson(response);
    } finally {
      session.destroy();
    }
  } catch {
    return [];
  }
}

export async function analyzeActivityCoverage(goalTitle: string, goalDescription: string, activityTitles: string[], customPrompts?: Record<string, string> | null): Promise<ActivityCoverageResult> {
  try {
    const activityList = activityTitles.map((a, i) => `${i + 1}. ${a}`).join('\n');
    const prompt = resolvePrompt('activity_coverage', customPrompts, { goalTitle, goalDescription, activityList }) ?? activityCoveragePrompt(goalTitle, goalDescription, activityTitles);
    const systemPrompt = `You are a planning coach. ${FORMAT_INSTRUCTIONS.activityCoverage}`;
    const session = await createSession(systemPrompt, 0.3);
    try {
      const response = await session.prompt(prompt);
      return parseActivityCoverageJson(response, activityTitles);
    } finally {
      session.destroy();
    }
  } catch {
    return { dimensions: [], summary: '' };
  }
}

export async function generateGapActivities(goalTitle: string, goalDescription: string, gapDimensions: string[], customPrompts?: Record<string, string> | null): Promise<GeneratedActivity[]> {
  try {
    const gapList = gapDimensions.map((d, i) => `${i + 1}. ${d}`).join('\n');
    const prompt = resolvePrompt('generate_gap_activities', customPrompts, { goalTitle, goalDescription, gapList }) ?? generateGapActivitiesPrompt(goalTitle, goalDescription, gapDimensions);
    const systemPrompt = `You are a planning coach. ${FORMAT_INSTRUCTIONS.generateGapActivities}`;
    const session = await createSession(systemPrompt, 0.4);
    try {
      const response = await session.prompt(prompt);
      return parseGapActivitiesJson(response);
    } finally {
      session.destroy();
    }
  } catch {
    return [];
  }
}

function parseActivityCoverageJson(response: string, activityTitles: string[]): ActivityCoverageResult {
  const jsonStr = response.replace(/```json?\s*/g, '').replace(/```/g, '').trim();
  const parsed = JSON.parse(jsonStr);

  const dimensions: ActivityCoverageResult['dimensions'] = [];
  if (Array.isArray(parsed.dimensions)) {
    for (const dim of parsed.dimensions) {
      const status = String(dim.status).toLowerCase() === 'covered' ? 'covered' as const : 'gap' as const;
      const activities: string[] = [];
      if (status === 'covered' && Array.isArray(dim.activity_indices)) {
        for (const idx of dim.activity_indices) {
          const num = Number(idx);
          if (num >= 1 && num <= activityTitles.length) {
            activities.push(activityTitles[num - 1]);
          }
        }
      }
      dimensions.push({ name: String(dim.name || ''), status, activities });
    }
  }

  return { dimensions, summary: String(parsed.summary || '') };
}

function parseGapActivitiesJson(response: string): GeneratedActivity[] {
  const jsonStr = response.replace(/```json?\s*/g, '').replace(/```/g, '').trim();
  const parsed = JSON.parse(jsonStr);
  if (!Array.isArray(parsed)) return [];
  return parsed.map((a: any) => ({
    title: String(a.title || ''),
    description: String(a.description || ''),
    estimated_minutes: Math.max(15, Math.min(240, Number(a.estimated_minutes) || 60)),
    relative_start_days: Math.max(0, Number(a.relative_start_days) || 0),
  }));
}

function parseCoverageJson(response: string, goalTitles: string[]): GoalCoverageResult {
  const jsonStr = response.replace(/```json?\s*/g, '').replace(/```/g, '').trim();
  const parsed = JSON.parse(jsonStr);

  const dimensions: GoalCoverageResult['dimensions'] = [];
  if (Array.isArray(parsed.dimensions)) {
    for (const dim of parsed.dimensions) {
      const status = String(dim.status).toLowerCase() === 'covered' ? 'covered' as const : 'gap' as const;
      const goals: string[] = [];
      if (status === 'covered' && Array.isArray(dim.goal_indices)) {
        for (const idx of dim.goal_indices) {
          const num = Number(idx);
          if (num >= 1 && num <= goalTitles.length) {
            goals.push(goalTitles[num - 1]);
          }
        }
      }
      dimensions.push({ name: String(dim.name || ''), status, goals });
    }
  }

  return { dimensions, summary: String(parsed.summary || '') };
}

function parseGapGoalsJson(response: string): GeneratedGoal[] {
  const jsonStr = response.replace(/```json?\s*/g, '').replace(/```/g, '').trim();
  const parsed = JSON.parse(jsonStr);
  if (!Array.isArray(parsed)) return [];
  return parsed.map((g: any) => ({
    title: String(g.title || ''),
    description: String(g.description || ''),
    measures: Array.isArray(g.measures) ? g.measures.map((m: any) => ({
      name: String(m.name || ''),
      unit: String(m.unit || ''),
      start_value: Number(m.start_value) || 0,
      target_value: Number(m.target_value) || 0,
    })) : [],
    milestone_count: Math.max(1, Math.min(52, Number(g.milestone_count) || 4)),
    start_date: String(g.start_date || new Date().toISOString().split('T')[0]),
    end_date: String(g.end_date || ''),
  }));
}

function parseReflectResponse(response: string): ReflectResult {
  const parts = response.split(/highlights?:/i);
  const reflection = (parts[0] ?? '').trim();
  const highlights: string[] = [];
  if (parts[1]) {
    for (const line of parts[1].split('\n')) {
      const trimmed = line.trim().replace(/^-\s*/, '');
      if (trimmed) highlights.push(trimmed);
    }
  }
  return { reflection, highlights };
}
